import { Button, DatePicker, Form, Input, Select } from 'antd';
import { ChangeEvent, FunctionComponent, useCallback, useState, useEffect } from 'react';
import css from './TribesFilters.module.less';
import useTranslate from '@hooks/useTranslate';
import componentContent from './TribesFilters.content.json';
import { DATE_FORMAT } from '@config/format';
import { ClearOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { RangePickerProps } from 'antd/lib/date-picker';
import moment from 'moment';
import {
  parseTribesFilters,
  TribesFiltersValue,
  tribeSortOptions as SORT_PARAMS,
} from '@helpers/tribesFilters';
import FilterPanel from '@components/FilterPanel/FilterPanel';

interface TribesFilterProps {
  filters: TribesFiltersValue;
  setFilters: (filters: TribesFiltersValue) => void;
}

const { RangePicker } = DatePicker;

const isSort = (key: { asc: string; desc: string }, value: string) => {
  return value === key.asc;
};

const TribesFilters: FunctionComponent<TribesFilterProps> = ({ filters, setFilters }) => {
  const content = useTranslate(componentContent);

  const [tribeNameFilter, setTribeNameFilter] = useState<string>('');
  const [linkFilter, setLinkFilter] = useState<string>('');
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [memberFilter, setMemberFilter] = useState<string>('');
  const [gameFilter, setGameFilter] = useState<string>('');

  const clearFilters = () => {
    setTribeNameFilter('');
    setLinkFilter('');
    setOwnerFilter('');
    setMemberFilter('');
    setGameFilter('');
    setMembersMinInput('');
    setMembersMaxInput('');
    setFilters(parseTribesFilters({}));
  };

  useEffect(() => setTribeNameFilter(filters.tribeName), [filters.tribeName]);
  useEffect(() => setLinkFilter(filters.url), [filters.url]);
  useEffect(() => setOwnerFilter(filters.owner), [filters.owner]);
  useEffect(() => setMemberFilter(filters.member), [filters.member]);
  useEffect(() => setGameFilter(filters.game), [filters.game]);
  const toggleSort = useCallback(
    (asc: string, desc: string) => {
      const next = filters.sort === desc ? asc : desc;
      setFilters({ ...filters, sort: next });
    },
    [filters, setFilters],
  );

  const [membersMinInput, setMembersMinInput] = useState<string>('');
  const [membersMaxInput, setMembersMaxInput] = useState<string>('');

  useEffect(() => {
    setMembersMinInput(filters.membersMin === undefined ? '' : String(filters.membersMin));
    setMembersMaxInput(filters.membersMax === undefined ? '' : String(filters.membersMax));
  }, [filters.membersMin, filters.membersMax]);

  const applyMembers = useCallback(() => {
    const min = membersMinInput.trim() === '' ? undefined : Number(membersMinInput);
    const max = membersMaxInput.trim() === '' ? undefined : Number(membersMaxInput);

    setFilters({
      ...filters,
      membersMin: Number.isFinite(min as number) ? (min as number) : undefined,
      membersMax: Number.isFinite(max as number) ? (max as number) : undefined,
    });
  }, [membersMinInput, membersMaxInput, filters, setFilters]);

  const searchByOwner = (owner: string) => setFilters({ ...filters, owner: owner.trim() });
  const searchByMember = (member: string) => setFilters({ ...filters, member: member.trim() });
  const searchByGame = (game: string) => setFilters({ ...filters, game: game.trim() });

  const onHasClaimsChange = (value: boolean | 'ALL') => {
    const hasClaims = value === 'ALL' ? undefined : value;

    const sort = hasClaims ? 'LAST_CLAIM_DATE_DESC' : 'MONTHLY_MESSAGES_COUNT_DESC';

    setFilters({ ...filters, hasClaims, sort });
  };

  const onNameFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTribeNameFilter(e.currentTarget.value);
  };

  const searchByName = (name: string) => {
    setFilters({ ...filters, tribeName: name.trim() });
  };

  const onLinkFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLinkFilter(e.currentTarget.value);
  };

  const searchByLink = (url: string) => {
    setFilters({ ...filters, url: url.trim() });
  };

  const onChangeDateRange = useCallback(
    (range: RangePickerProps['value']) => {
      const [dateStart, dateEnd] = range ?? [];
      let createdFrom, createdTo;

      if (dateStart) createdFrom = dateStart.startOf('day').format();
      if (dateEnd) createdTo = dateEnd.startOf('day').format();
      setFilters({
        ...filters,
        createdFrom,
        createdTo,
      });
    },
    [filters, setFilters],
  );

  return (
    <Form layout='vertical'>
      <div className={css.filters}>
        <FilterPanel
          actions={
            <Button
              htmlType='button'
              icon={<ClearOutlined />}
              onClick={clearFilters}
            >
              {content.clear}
            </Button>
          }
        >
          <Form.Item label={content.status}>
            <Select
              aria-label={content.status}
              value={String(filters.isBlocked)}
              onChange={(value) => setFilters({ ...filters, isBlocked: value === 'true' })}
              className={css.filterInput}
              options={[
                { value: 'false', label: content.unblocked },
                { value: 'true', label: content.blocked },
              ]}
            />
          </Form.Item>

          <Form.Item label={content.search_by_name}>
            <Input.Search
              allowClear
              className={css.filterInput}
              value={tribeNameFilter}
              onSearch={searchByName}
              onChange={onNameFilterChange}
            />
          </Form.Item>

          <Form.Item label={content.search_by_url}>
            <Input.Search
              allowClear
              className={css.filterInput}
              value={linkFilter}
              onSearch={searchByLink}
              onChange={onLinkFilterChange}
            />
          </Form.Item>

          <Form.Item label={content.choose_date}>
            <RangePicker
              className={css.dateRangeSelector}
              format={DATE_FORMAT}
              allowEmpty={[true, true]}
              value={[
                filters.createdFrom ? moment(filters.createdFrom) : null,
                filters.createdTo ? moment(filters.createdTo) : null,
              ]}
              placeholder={[content.rangePlaceholderFrom, content.rangePlaceholderTo]}
              onChange={onChangeDateRange}
            />
          </Form.Item>

          <Form.Item label={content.search_by_nickname}>
            <Input.Search
              allowClear
              className={css.filterInput}
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.currentTarget.value)}
              onSearch={searchByOwner}
            />
          </Form.Item>

          <Form.Item label={content.search_by_member}>
            <Input.Search
              allowClear
              className={css.filterInput}
              value={memberFilter}
              onChange={(e) => setMemberFilter(e.currentTarget.value)}
              onSearch={searchByMember}
            />
          </Form.Item>

          <Form.Item label={content.search_by_game}>
            <Input.Search
              allowClear
              className={css.filterInput}
              value={gameFilter}
              onChange={(e) => setGameFilter(e.currentTarget.value)}
              onSearch={searchByGame}
            />
          </Form.Item>

          <Form.Item label={content.members_min}>
            <Input
              className={css.filterInput}
              value={membersMinInput}
              onChange={(e) => setMembersMinInput(e.currentTarget.value)}
              onPressEnter={applyMembers}
              allowClear
            />
          </Form.Item>

          <Form.Item label={content.members_max}>
            <Input
              className={css.filterInput}
              value={membersMaxInput}
              onChange={(e) => setMembersMaxInput(e.currentTarget.value)}
              onPressEnter={applyMembers}
              allowClear
            />
          </Form.Item>

          <Form.Item label={content.has_claims}>
            <Select
              value={filters.hasClaims === undefined ? 'ALL' : filters.hasClaims}
              onChange={onHasClaimsChange}
              className={css.filterInput}
              options={[
                { value: 'ALL', label: content.has_claims_all },
                { value: true, label: content.has_claims_yes },
                { value: false, label: content.has_claims_no },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Button
              onClick={() => toggleSort(SORT_PARAMS.claimsCount.asc, SORT_PARAMS.claimsCount.desc)}
              type='link'
              style={{ margin: 0, padding: 0 }}
            >
              <span>{content.sort_by_claims_count}</span>
              {isSort(SORT_PARAMS.claimsCount, filters.sort) ? (
                <SortAscendingOutlined />
              ) : (
                <SortDescendingOutlined />
              )}
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              onClick={() => toggleSort(SORT_PARAMS.monthly.asc, SORT_PARAMS.monthly.desc)}
              type='link'
              style={{ margin: 0, padding: 0 }}
            >
              <span>{content.sort_by_monthly_messages}</span>
              {isSort(SORT_PARAMS.monthly, filters.sort) ? (
                <SortAscendingOutlined />
              ) : (
                <SortDescendingOutlined />
              )}
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              onClick={() => toggleSort(SORT_PARAMS.claims.asc, SORT_PARAMS.claims.desc)}
              type='link'
              style={{ margin: 0, padding: 0 }}
            >
              <span>{content.sort_by_last_claim}</span>
              {isSort(SORT_PARAMS.claims, filters.sort) ? (
                <SortAscendingOutlined />
              ) : (
                <SortDescendingOutlined />
              )}
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              onClick={() => toggleSort(SORT_PARAMS.game.asc, SORT_PARAMS.game.desc)}
              type='link'
              style={{ margin: 0, padding: 0 }}
            >
              <span>{content.sort_by_game}</span>
              {isSort(SORT_PARAMS.game, filters.sort) ? (
                <SortAscendingOutlined />
              ) : (
                <SortDescendingOutlined />
              )}
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              onClick={() => toggleSort(SORT_PARAMS.id.asc, SORT_PARAMS.id.desc)}
              type='link'
              style={{ margin: 0, padding: 0 }}
            >
              <span>{content.sort_by_id}</span>
              {isSort(SORT_PARAMS.id, filters.sort) ? (
                <SortAscendingOutlined />
              ) : (
                <SortDescendingOutlined />
              )}
            </Button>
          </Form.Item>
        </FilterPanel>
      </div>
    </Form>
  );
};

export default TribesFilters;
